/**
 * sync-store.ts — Zustand store for offline sales queue
 *
 * When the POS is offline, completed sales are queued here.
 * When connectivity is restored, the queue is flushed to the API
 * in FIFO order (oldest first) to preserve chronological integrity.
 *
 * Conflict resolution rules (from MIGRATION_PLAN.md § 3.3.5):
 * - Duplicate detection: sale UUIDs are client-generated → idempotent retries
 * - Inventory negative: server is source of truth, sale still processes
 * - Price changes: server revalidates prices on replay (cutover risk documented)
 * - All conflicts logged to `sync_conflicts` table via API
 *
 * Serialization: queue items are processed ONE AT A TIME (sequential, not parallel)
 * to avoid inventory conflicts between offline sales from the same branch.
 *
 * Cola de prod: WebApp/Scripts/App/offline/kapi-offline-ventas-pos.js.
 */

import { create } from 'zustand';
import type { WithHydration } from './middleware/persist';
import { updateSyncQueue } from '../lib/idb';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Status of a queued sale */
export type QueueItemStatus =
  | 'pending'   // Waiting to be sent
  | 'sending'   // Currently being sent
  | 'sent'      // Successfully processed by server
  | 'failed'    // Sending failed (will retry)
  | 'error';    // Permanent error (needs manual review)

/** Maximum retry attempts before marking as error */
const MAX_RETRIES = 5;

/** Default offline window in milliseconds (4 hours per MIGRATION_PLAN) */
const OFFLINE_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * An offline sale queued for later sync.
 */
export interface OfflineSaleEntry {
  /** Client-generated UUID — used for idempotency */
  id: string;
  /** ISO timestamp when the sale was completed offline */
  createdAt: string;
  /** Serialized sale payload (SalePaymentRequest as JSON-safe object) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: Record<string, any>;
  /** Branch ID (for routing to correct API endpoint) */
  branchId: string;
  /** Box number */
  box: number;
  /** Current sync status */
  status: QueueItemStatus;
  /** Number of send attempts */
  attempts: number;
  /** Error from last failed attempt */
  lastError?: string;
  /** ISO timestamp of last attempt */
  lastAttemptAt?: string;
  /** Server-assigned sale ID (once successfully sent) */
  serverSaleId?: number;
}

export interface SyncState {
  /** FIFO queue of offline sales */
  queue: OfflineSaleEntry[];
  /** Whether the app is currently in offline mode */
  isOffline: boolean;
  /** Whether sync is currently running */
  isSyncing: boolean;
  /** ISO timestamp of last successful sync */
  lastSyncAt: string | null;
  /** Number of items successfully synced */
  totalSynced: number;
  /** Number of items that failed permanently */
  totalFailed: number;
}

export interface SyncActions {
  /**
   * Mark the app as online or offline.
   * When going online, trigger sync automatically if queue is non-empty.
   */
  setOnline(online: boolean): void;

  /**
   * Enqueue a completed offline sale.
   */
  enqueue(entry: Omit<OfflineSaleEntry, 'status' | 'attempts' | 'createdAt'>): Promise<void>;

  /**
   * Process the queue — send pending items to the API (FIFO, sequential).
   * The caller provides the send function to avoid circular imports.
   *
   * @param sendFn - Async function that POSTs a single sale to the API.
   *                 Should throw on failure.
   */
  flush(
    sendFn: (entry: OfflineSaleEntry) => Promise<number>,
  ): Promise<void>;

  /**
   * Get all pending (unsent) items.
   */
  getPending(): OfflineSaleEntry[];

  /**
   * Get count of items still pending.
   */
  pendingCount(): number;

  /**
   * Remove successfully sent items from the queue.
   */
  clearSent(): Promise<void>;

  /** Acción «Registrar» de prod: devuelve los errores a la cola con intentos en cero. */
  retryErrors(): Promise<void>;

  /**
   * Check if the oldest pending item exceeds the offline window.
   * Returns true if a warning should be shown.
   */
  isOfflineWindowExceeded(): boolean;
}

export type SyncStore = SyncState & SyncActions & WithHydration;

// Las mutaciones leen la cola durable dentro de UNA transacción: una pestaña nunca
// sobrescribe ventas de otra con su snapshot anterior.
export const useSyncStore = create<SyncStore>()((set, get) => {
  const update = async (change: (rows: Map<string, OfflineSaleEntry>) => void = () => {}) => {
    const queue = await updateSyncQueue<OfflineSaleEntry>(rows => {
      const legacy = rows.get('state') as unknown as { data?: { queue?: OfflineSaleEntry[] } } | undefined;
      if (legacy) {
        for (const entry of legacy.data?.queue ?? []) if (!rows.has(entry.id)) rows.set(entry.id, entry);
        rows.delete('state');
      }
      for (const [id, entry] of rows) if (entry.status === 'sending') rows.set(id, { ...entry, status: 'pending', attempts: Math.max(0, entry.attempts - 1) });
      change(rows);
    });
    set({ queue: queue.sort((a, b) => a.createdAt.localeCompare(b.createdAt)) });
  };
  const patch = async (id: string, changes: Partial<OfflineSaleEntry>) => update(rows => {
    const current = rows.get(id);
    // Otra pestaña pudo confirmar el mismo GUID mientras esta esperaba la respuesta.
    if (current && current.status !== 'sent') rows.set(id, { ...current, ...changes });
  });
  const ready = Promise.resolve().then(() => update()).catch(err => {
    console.error('[sync-store] No se pudo leer la cola:', err);
  }).finally(() => set({ _hydrated: true }));
  return {
    queue: [], isOffline: false, isSyncing: false, lastSyncAt: null,
    totalSynced: 0, totalFailed: 0, _hydrated: false,
    setOnline(online) { set({ isOffline: !online }); },
    async enqueue(entry) {
      await ready;
      await update(rows => {
        if (!rows.has(entry.id)) rows.set(entry.id, {
          ...entry, createdAt: new Date().toISOString(), status: 'pending', attempts: 0,
        });
      });
    },
    async flush(sendFn) {
      if (get().isSyncing) return;
      set({ isSyncing: true });
      try {
        await ready;
        await update();
        for (const queued of get().getPending()) {
          // Releer por cada envío también reconoce los acuses de otra pestaña.
          await update();
          const item = get().queue.find(e => e.id === queued.id);
          if (!item || !['pending', 'failed'].includes(item.status)) continue;
          if (item.attempts >= MAX_RETRIES) { await patch(item.id, { status: 'error' }); continue; }
          // Como prod, permanece pending durante el envío. Un cierre conserva el GUID.
          const attempts = item.attempts + 1;
          try {
            const serverSaleId = await sendFn(item);
            await patch(item.id, { status: 'sent', serverSaleId, attempts, lastAttemptAt: new Date().toISOString() });
            set(state => ({ totalSynced: state.totalSynced + 1, lastSyncAt: new Date().toISOString() }));
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Send failed';
            const permanent = (err as { code?: string })?.code === 'SesionCambiada' || attempts >= MAX_RETRIES;
            await patch(item.id, { status: permanent ? 'error' : 'failed', attempts, lastError: message, lastAttemptAt: new Date().toISOString() });
            if (permanent) set(state => ({ totalFailed: state.totalFailed + 1 }));
          }
        }
      } finally { set({ isSyncing: false }); }
    },
    getPending() { return get().queue.filter(e => ['pending', 'failed', 'sending'].includes(e.status)); },
    pendingCount() { return get().getPending().length; },
    async clearSent() { await ready; await update(rows => { for (const [id, e] of rows) if (e.status === 'sent') rows.delete(id); }); },
    async retryErrors() {
      await ready;
      await update(rows => { for (const [id, e] of rows) if (e.status === 'error') rows.set(id, { ...e, status: 'pending', attempts: 0, lastError: undefined }); });
    },
    isOfflineWindowExceeded() {
      const oldest = get().getPending()[0];
      return !!oldest && Date.now() - new Date(oldest.createdAt).getTime() > OFFLINE_WINDOW_MS;
    },
  };
});
